import type { AccountEventRequest } from "@storyboard/web-client";
import { createAccountEvents } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";

const log = createLogger("events");

/** How long the first receipt of an empty batch waits for company before the batch is sent. */
export const SEEN_RECEIPT_FLUSH_MS = 250;

/** The most receipts one request carries; reaching it sends the batch at once. */
export const SEEN_RECEIPT_MAX_BATCH = 50;

let pending: AccountEventRequest[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

/**
 * Adds a fully built "Saw Feed Item" request to the pending batch.
 *
 * The first receipt of an empty batch starts a {@link SEEN_RECEIPT_FLUSH_MS} window; the batch reaching
 * {@link SEEN_RECEIPT_MAX_BATCH} sends it immediately.
 */
export function queueSeenReceipt(event: AccountEventRequest): void {
  listenForPageHide();
  pending.push(event);
  if (pending.length >= SEEN_RECEIPT_MAX_BATCH) {
    void flushSeenReceipts();
    return;
  }
  flushTimer ??= setTimeout(() => void flushSeenReceipts(), SEEN_RECEIPT_FLUSH_MS);
}

/**
 * Sends the pending batch now, in one `POST /v1/account-events`.
 *
 * A failed request is logged and dropped, not retried: a lost seen receipt costs one stale read state, as a failed
 * single-event send always has.
 */
export async function flushSeenReceipts(): Promise<void> {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.length === 0) return;
  const data = pending;
  pending = [];
  try {
    await createAccountEvents(transport, { data });
  } catch (error) {
    log.warn("Seen receipt batch failed", { count: data.length, error });
  }
}

/** Sends whatever is pending as the tab hides or unloads, so a closing tab does not strand its last receipts. */
function listenForPageHide(): void {
  if (listening || typeof document === "undefined" || typeof window === "undefined") return;
  listening = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushSeenReceipts();
  });
  window.addEventListener("pagehide", () => void flushSeenReceipts());
}
